import {Injectable} from '@angular/core';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Graphic from '@arcgis/core/Graphic';
import Polyline from '@arcgis/core/geometry/Polyline';
import {GenericPopupService} from '@core/utils/engin/geospatial-viewer/popups/generic-popup.service';
import {SpatialOutageEntityDataExtended} from '@core/utils/engin/geospatial-viewer/helper/engin-data-prepare.service';
import {OutageFieldsService} from '@core/utils/engin/geospatial-viewer/fields/outage-fields.service';
import {OutageRendererService} from '@core/utils/engin/geospatial-viewer/renderers/outage-renderer.service';
import Point from '@arcgis/core/geometry/Point';
import {VisualizationType} from '../../../../../../pages/geospatial-viewer/model/visualization';

@Injectable()
export class OutagePolylineLayerService {
    constructor(
        private genericPopupService: GenericPopupService,
        private rendererService: OutageRendererService,
        private fieldsService: OutageFieldsService,
    ) {}

    /**
     * Prepare polyline layer (geometry type = polyline).
     * Works for point assets (lines) or regions (multi-lines).
     * @return FeatureLayer
     */
    public preparePolylineLayer(
        linearData: SpatialOutageEntityDataExtended[],
        renderedVisualizationType: VisualizationType,
        id: string,
        theme: string,
        numMetricLayers: number,
        metrics: any[] = [],
    ): FeatureLayer[] {
        // Load Esri dependencies from AMD
        const sourceLines = this.createPolylineGraphics(linearData);
        const sourcePoints = this.createLineCentroidPointGraphics(linearData);

        /*
         * Create one layer, simple lines with red-green colours
         */
        // Create the first layer: Line layer, with colour setting the size and colour of the line
        const linePopupTemplate = {
            title: '{id}',
            outFields: ['*'],
            content: this.genericPopupService.getGenericOutageTooltipFunction.bind(
                this.genericPopupService,
                metrics,
                renderedVisualizationType,
            ),
        };
        const lineLayerBackground = new FeatureLayer({
            id: id ? id : 'outage_line_layer' + '1',
            objectIdField: 'ObjectID',
            source: sourceLines,
            renderer: this.rendererService.outageLineRenderer(theme, numMetricLayers),
            fields: this.fieldsService.getMapFields(),
            outFields: ['*'],
            popupTemplate: linePopupTemplate,
        });

        const label = {
            labelExpressionInfo: {
                expression: '$feature.label',
            },
            symbol: {
                type: 'text',
                color: 'white',
                backgroundColor: 'transparent',
                yoffset: 0,
                font: {
                    weight: 'bold',
                    size: '12px',
                },
                haloColor: 'black',
                haloSize: 1,
            },
            labelPlacement: 'center-center',
        };
        const lineLayerLabel = new FeatureLayer({
            id: id ? id : 'outage_line_layer' + '3',
            objectIdField: 'ObjectID',
            source: sourcePoints,
            renderer: this.rendererService.noSymbolRenderer(),
            fields: this.fieldsService.getMapFields(),
            outFields: ['*'],
            labelingInfo: [
                {
                    ...label,
                    symbol: {
                        ...label.symbol,
                        // @ts-ignore
                        font: {
                            ...label.symbol.font,
                            size: '12px',
                        },
                    },
                    where: `colour = 'OUTAGE_COLOUR1'`,
                },
                {
                    ...label,
                    symbol: {
                        ...label.symbol,
                        // @ts-ignore
                        font: {
                            ...label.symbol.font,
                            size: '14px',
                        },
                    },
                    where: `colour = 'OUTAGE_COLOUR2'`,
                },
                {
                    ...label,
                    symbol: {
                        ...label.symbol,
                        // @ts-ignore
                        font: {
                            ...label.symbol.font,
                            size: '18px',
                        },
                    },
                    where: `colour = 'OUTAGE_COLOUR3'`,
                },
                {
                    ...label,
                    symbol: {
                        ...label.symbol,
                        // @ts-ignore
                        font: {
                            ...label.symbol.font,
                            size: '20px',
                        },
                    },
                    where: `colour = 'OUTAGE_COLOUR4'`,
                },
                {
                    ...label,
                    symbol: {
                        ...label.symbol,
                        // @ts-ignore
                        font: {
                            ...label.symbol.font,
                            size: '24px',
                        },
                    },
                    where: `colour = 'OUTAGE_COLOUR5'`,
                },
            ],
        });

        return [lineLayerBackground, lineLayerLabel];
    }

    /*
     * Create array of Graphics, type = polyline. Return Graphic[]
     */
    private createPolylineGraphics(lineData: SpatialOutageEntityDataExtended[]): Graphic[] {
        return lineData.map((area) => {
            const geometry = new Polyline({
                paths: area.geoJsonGeometry.coordinates,
            });

            return new Graphic({
                geometry: geometry,
                attributes: {
                    ...area,
                },
            });
        });
    }
    private createLineCentroidPointGraphics(lineData: SpatialOutageEntityDataExtended[]): Graphic[] {
        return lineData.map((outage: SpatialOutageEntityDataExtended) => {
            const point = new Point({
                latitude: outage.centroidGeoJsonGeometry.coordinates[1],
                longitude: outage.centroidGeoJsonGeometry.coordinates[0],
            });

            return new Graphic({
                geometry: point,
                attributes: {...outage},
            });
        });
    }
}
